import { ArrowLeft, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/shadcn/ui/button';
import { Switch } from '@/components/shadcn/ui/switch';
import {
  enableNotification,
  mapFormDataToSignal,
  mapSignalToFormData,
  SignalAlertFormContent,
  type SignalAlertFormData,
  signalAlertFormDefaultValues,
  signalAlertFormOptions,
  useCreateSignalAlertMutation,
  useUpdateSignalAlertMutation,
} from '@/entities/notification';
import { errorHandler } from '@/error-handler';
import { useSignalAlertTitles } from '@/features/(signal)/signal-alert/hooks/use-signal-alert-titles';
import {
  DEFAULT_SIGNAL_DATE_SELECTION,
  signalDatePresetToDurationDays,
} from '@/features/(signal)/signals/lib/signal-date-preset.utils';
import {
  type SignalDateSelection,
  storedSignalDateSelectionSchema,
} from '@/features/(signal)/signals/model';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useWorkspaceStorage from '@/hooks/pure/useWorkspaceStorage';
import { useAppForm } from '@/shared/ui/form';
import { toast } from '@/stores/pure/ToastStore';
import { WORKFLOW_TYPE } from '@/types/workflowConst';
import { createStorageParser } from '@/utils/storage';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import useWorkflowDetail from '../useNotiDetail';

type Props = {
  initialState?: SignalAlertWorkflow;
  mode: EDIT_MODE;
};

export default function SignalAlertWorkflowDetail({
  initialState,
  mode,
}: Props) {
  const wr = useWorkspaceRouter();
  const workspaceId = getCurrWorkspaceAlias();
  const [enable, setEnable] = useState(initialState?.enable ?? true);

  const createMutation = useCreateSignalAlertMutation();
  const updateMutation = useUpdateSignalAlertMutation();

  const { onDelete } = useWorkflowDetail({
    mode,
    alias: initialState?.alias,
    initChange() {
      // no-op
    },
  });

  const { existingTitles } = useSignalAlertTitles({
    excludeAlias: initialState?.alias,
  });

  const [dateSelection] = useWorkspaceStorage<SignalDateSelection>(
    'signals-date-preset',
    DEFAULT_SIGNAL_DATE_SELECTION,
    {
      parser: createStorageParser(
        storedSignalDateSelectionSchema,
        DEFAULT_SIGNAL_DATE_SELECTION,
      ),
    },
  );

  // Workflow 데이터를 form 데이터로 변환
  const getDefaultValues = (): SignalAlertFormData => {
    if (!initialState) {
      return {
        ...signalAlertFormDefaultValues,
        duration: signalDatePresetToDurationDays(dateSelection),
      };
    }

    return {
      ...mapSignalToFormData(initialState.signal, initialState.channel),
      title: initialState.title,
    };
  };

  // 저장 핸들러
  const handleSave = async (values: SignalAlertFormData) => {
    const payload = {
      title: values.title,
      enable,
      type: WORKFLOW_TYPE.SIGNAL_ALERT,
      signal: mapFormDataToSignal(values),
      channel: values.channel,
    };

    if (mode === 'CREATE') {
      createMutation.mutate(payload, {
        onSuccess: () => {
          toast('Workflow created', 'success');
          wr.push('/workflow');
        },
        onError: (error) => {
          toast(errorHandler(error, 'Failed to create workflow'), 'fail');
        },
      });
    } else {
      updateMutation.mutate(
        {
          notification_id: initialState!.alias,
          ...payload,
        },
        {
          onSuccess: () => {
            toast('Workflow updated', 'success');
            wr.push('/workflow');
          },
          onError: (error) => {
            toast(errorHandler(error, 'Failed to update workflow'), 'fail');
          },
        },
      );
    }
  };

  const form = useAppForm({
    ...signalAlertFormOptions,
    defaultValues: getDefaultValues(),
    onSubmit: async ({ value }) => {
      await handleSave(value);
    },
  });

  // 토글 핸들러
  const handleToggle = async (status: boolean) => {
    if (!initialState) return;
    try {
      await enableNotification({
        notification_id: initialState.alias,
        enable: status,
      });
      setEnable(status);
      toast(status ? 'Workflow enabled' : 'Workflow disabled', 'success');
    } catch (error) {
      toast(errorHandler(error, 'Failed to toggle workflow'), 'fail');
    }
  };

  return (
    <div className="flex h-screen flex-col overflow-hidden max-w-[1400px]">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-4 py-2 mb-6">
        <Button
          variant="outline"
          size="icon"
          onClick={() => wr.push('/workflow')}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <div className="flex items-center gap-6">
          {mode === 'EDIT' && (
            <>
              <Switch checked={enable} onCheckedChange={handleToggle} />
              <Button variant="ghost" size="icon" onClick={onDelete}>
                <Trash2 className="size-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* 본문 */}
      <SignalAlertFormContent
        form={form}
        workspaceId={workspaceId}
        existingTitles={existingTitles}
        renderFooter={() => (
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
          >
            {([canSubmit, isSubmitting]) => (
              <div className="flex gap-2">
                <Button
                  className="w-[100px]"
                  onClick={form.handleSubmit}
                  disabled={!canSubmit || isSubmitting}
                >
                  Save
                </Button>
              </div>
            )}
          </form.Subscribe>
        )}
      />
    </div>
  );
}
